/**
 * The journal: level, challenges, and every collection, in one sheet.
 *
 * ## Reusable by design
 *
 * Like `src/progression/`, this knows nothing about gardens. It renders a
 * `JournalView` — a level, some challenge groups, and some collection
 * sections — and the game builds that view from its own content. A new
 * project changes what goes into the view, not this file.
 *
 * Plain DOM, for the same reasons as `UI.ts`: it lives inside the WebXR
 * `dom-overlay` root. It is rebuilt with `innerHTML` on each open because it
 * is small, it is only ever up while nothing else is happening, and
 * reconciling a few dozen rows by hand would be more code than it saves.
 *
 * **Every string from the view is escaped.** The one exception is
 * `CollectionItem.icon`, which is markup the game generates itself (an inline
 * SVG or an emoji) and must never be built from anything a player typed.
 */

export interface JournalLevel {
  level: number;
  rank: string;
  rankAr: string;
  xp: number;
  /** XP at the start of this level and at the next; `next` null at the cap. */
  floor: number;
  next: number | null;
  progress: number;
  /** A line under the bar, e.g. what the next level pays. */
  note: string;
}

export interface JournalChallenge {
  id: string;
  title: string;
  titleAr: string;
  value: number;
  target: number;
  xp: number;
  done: boolean;
}

export interface JournalChallengeGroup {
  heading: string;
  /** e.g. "resets in 5h 12m". */
  meta: string;
  rows: JournalChallenge[];
}

export interface CollectionItem {
  id: string;
  /** Trusted, game-generated markup. See the note at the top of this file. */
  icon: string;
  name: string;
  sub?: string;
  detail?: string;
  earned: boolean;
  /** Optional accent colour for the earned state, as a CSS colour. */
  tint?: string;
  /** Tapping an earned item runs this, e.g. re-reading a fact card. */
  open?: () => void;
}

export interface CollectionSection {
  id: string;
  /** Which tab it belongs under. Tabs appear in order of first use. */
  tab: string;
  title: string;
  layout: 'list' | 'grid';
  items: CollectionItem[];
}

export interface JournalView {
  kicker: string;
  level: JournalLevel;
  challengesTab: string;
  challenges: JournalChallengeGroup[];
  emptyChallenges: string;
  sections: CollectionSection[];
  xpLabel: string;
}

export interface JournalCallbacks {
  onClose(): void;
  onRegister(): void;
}

function escapeHtml(value: string | number): string {
  return String(value).replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] ?? c,
  );
}

const fmt = new Intl.NumberFormat('en-GB');

export class Journal {
  private root: HTMLElement;
  private body: HTMLElement;
  private tabs: HTMLElement;
  private tab: string | null = null;
  private openers = new Map<string, () => void>();
  private view: JournalView | null = null;

  constructor(root: HTMLElement, cb: JournalCallbacks) {
    this.root = root;
    this.body = root.querySelector<HTMLElement>('[data-journal-body]')!;
    this.tabs = root.querySelector<HTMLElement>('[data-journal-tabs]')!;

    root.querySelector('[data-journal-close]')?.addEventListener('click', () => cb.onClose());
    root.querySelector('[data-journal-register]')?.addEventListener('click', () => cb.onRegister());

    this.tabs.addEventListener('click', (event) => {
      const button = (event.target as HTMLElement).closest<HTMLElement>('[data-tab]');
      if (!button || !this.view) return;
      this.tab = button.dataset.tab ?? null;
      this.render(this.view);
      // The sheet scrolls, not the body; a new tab should open at its top.
      this.tabs.scrollIntoView({ block: 'nearest' });
    });

    // One delegated listener; the rows are rebuilt on every render. Its own
    // attribute, because `data-open` is what every modal scrim uses for state
    // and `closest()` walks straight past the body into one.
    this.body.addEventListener('click', (event) => {
      const item = (event.target as HTMLElement).closest<HTMLElement>('[data-collect-open]');
      const open = item ? this.openers.get(item.dataset.collectOpen ?? '') : undefined;
      open?.();
    });
  }

  get isOpen(): boolean {
    return this.root.dataset.open === 'true';
  }

  open(view: JournalView): void {
    this.render(view);
    this.root.dataset.open = 'true';
    this.root.setAttribute('aria-hidden', 'false');
  }

  close(): void {
    this.root.dataset.open = 'false';
    this.root.setAttribute('aria-hidden', 'true');
  }

  /** Re-render in place if open — progress can move while it is up. */
  refresh(view: JournalView): void {
    if (this.isOpen) this.render(view);
  }

  private render(view: JournalView): void {
    this.view = view;
    const tabNames = [view.challengesTab, ...new Set(view.sections.map((s) => s.tab))];
    if (!this.tab || !tabNames.includes(this.tab)) this.tab = tabNames[0];

    this.root.querySelector('[data-journal-kicker]')!.textContent = view.kicker;
    this.renderLevel(view);

    this.tabs.innerHTML = tabNames
      .map(
        (name) =>
          `<button type="button" class="journal-tab" role="tab" data-tab="${escapeHtml(name)}"
             aria-selected="${name === this.tab}">${escapeHtml(name)}</button>`,
      )
      .join('');

    this.openers.clear();
    this.body.innerHTML =
      this.tab === view.challengesTab
        ? this.challengesHtml(view)
        : view.sections
            .filter((s) => s.tab === this.tab)
            .map((s) => this.sectionHtml(s))
            .join('');
  }

  private renderLevel(view: JournalView): void {
    const { level } = view;
    const card = this.root.querySelector<HTMLElement>('[data-journal-level]')!;
    const pct = Math.round(Math.max(0, Math.min(1, level.progress)) * 100);
    const span = level.next === null ? '' : ` / ${fmt.format(level.next)}`;
    card.innerHTML = `
      <div class="level-ring level-ring--lg" style="--p:${pct}">
        <span class="level-ring-num">${level.level}</span>
      </div>
      <div class="level-text">
        <p class="level-rank">${escapeHtml(level.rank)}
          <span class="level-rank-ar" lang="ar" dir="rtl">${escapeHtml(level.rankAr)}</span></p>
        <div class="xp-bar"><div class="xp-fill" style="width:${pct}%"></div></div>
        <p class="level-xp">${fmt.format(level.xp)}${span} ${escapeHtml(view.xpLabel)}</p>
        <p class="level-note">${escapeHtml(level.note)}</p>
      </div>`;
  }

  private challengesHtml(view: JournalView): string {
    const groups = view.challenges.filter((g) => g.rows.length > 0);
    if (groups.length === 0) {
      return `<p class="journal-empty">${escapeHtml(view.emptyChallenges)}</p>`;
    }
    return groups
      .map(
        (group) => `
        <div class="journal-group">
          <p class="sheet-sub journal-sub">${escapeHtml(group.heading)}
            <span class="journal-meta">${escapeHtml(group.meta)}</span></p>
          <ul class="challenge-list">
            ${group.rows.map((row) => this.challengeHtml(row, view.xpLabel)).join('')}
          </ul>
        </div>`,
      )
      .join('');
  }

  private challengeHtml(row: JournalChallenge, xpLabel: string): string {
    const pct = row.target > 0 ? Math.round((row.value / row.target) * 100) : 0;
    return `
      <li class="challenge" data-done="${row.done}">
        <span class="challenge-check" aria-hidden="true">${row.done ? '✓' : ''}</span>
        <span class="challenge-main">
          <span class="challenge-title">${escapeHtml(row.title)}</span>
          <span class="challenge-ar" lang="ar" dir="rtl">${escapeHtml(row.titleAr)}</span>
          <span class="challenge-bar"><span class="challenge-fill" style="width:${pct}%"></span></span>
        </span>
        <span class="challenge-side">
          <span class="challenge-count">${row.value}/${row.target}</span>
          <span class="challenge-xp">+${row.xp} ${escapeHtml(xpLabel)}</span>
        </span>
      </li>`;
  }

  private sectionHtml(section: CollectionSection): string {
    const earned = section.items.filter((i) => i.earned).length;
    const items = section.items
      .map((item) => {
        const key = `${section.id}:${item.id}`;
        const canOpen = item.earned && item.open;
        if (canOpen) this.openers.set(key, item.open!);
        const tint = item.tint ? ` style="--item-tint:${escapeHtml(item.tint)}"` : '';
        const tag = canOpen ? 'button' : 'div';
        const attrs = canOpen ? ` type="button" data-collect-open="${escapeHtml(key)}"` : '';
        return `
          <${tag} class="collect collect--${section.layout}" data-earned="${item.earned}"${attrs}${tint}>
            <span class="collect-icon" aria-hidden="true">${item.icon}</span>
            <span class="collect-text">
              <span class="collect-name">${escapeHtml(item.name)}</span>
              ${item.sub ? `<span class="collect-sub">${escapeHtml(item.sub)}</span>` : ''}
              ${item.detail ? `<span class="collect-detail">${escapeHtml(item.detail)}</span>` : ''}
            </span>
          </${tag}>`;
      })
      .join('');
    return `
      <div class="journal-group">
        <p class="sheet-sub journal-sub">${escapeHtml(section.title)}
          <span class="journal-meta">${earned} / ${section.items.length}</span></p>
        <div class="collect-${section.layout}">${items}</div>
      </div>`;
  }
}
